/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSection } from '@/shared/ui/components/form';
import { ChipSelectMulti } from '@/shared/ui/components/form/ChipSelect';
import { getCategoryConfig, CONDO_AMENITIES, CONDO_SECURITY, INFRASTRUCTURE_ITEMS } from './schema';
import { translateSchemaItem, type SchemaItemGroup } from '../PropertyFormDualPane/schema.constants';
import type { PropertyFormValues } from './schema';

interface AmenitiesSectionProps {
  form: UseFormReturn<PropertyFormValues>;
}

/**
 * Renders the amenities selection section of the property drawer form.
 * @returns The amenities section element.
 */
export function AmenitiesSection({ form }: AmenitiesSectionProps) {
  const { t } = useTranslation('features/properties');
  const category = form.watch('category');
  const isCondominium = form.watch('condominium');
  const config = getCategoryConfig(category);

  const hasLeisure = config.leisure.length > 0;
  const hasFacilities = config.facilities.length > 0;
  const hasSecurity = config.security.length > 0;

  // Values stay the backend's tokens; only the label is translated. Anything
  // the listing holds outside this category's list is offered too, already
  // selected, so it can be seen and removed instead of riding along unseen.
  const toOptions = (tokens: readonly string[], group: SchemaItemGroup, selected?: string[]) => {
    const extras = (selected ?? []).filter(value => !tokens.includes(value));
    return [...tokens, ...extras].map(token => ({ value: token, label: translateSchemaItem(t, group, token) }));
  };
  const leisureOptions = toOptions(config.leisure, 'leisureAreas', form.watch('leisure_areas'));
  const facilitiesOptions = toOptions(config.facilities, 'facilities', form.watch('facilities'));
  const securityOptions = toOptions(config.security, 'securityItems', form.watch('security'));
  const condoAmenityOptions = toOptions(CONDO_AMENITIES, 'condoAmenities', form.watch('condo_amenities'));
  const condoSecurityOptions = toOptions(CONDO_SECURITY, 'condoSecurity', form.watch('condo_security'));
  // Free text, as the previous systems wrote it ("Hidrômetro individual"):
  // shown as stored, with whatever the listing already holds offered too.
  const infrastructureOptions = [...new Set([...INFRASTRUCTURE_ITEMS, ...(form.watch('infrastructure') ?? [])])]
    .map(item => ({ value: item, label: item }));

  return (
    <FormSection title={t('propertyDrawer.amenities.title')}>
      <Controller
        name="infrastructure"
        control={form.control}
        render={({ field }) => (
          <ChipSelectMulti
            label={t('propertyDrawer.amenities.infrastructure')}
            options={infrastructureOptions}
            value={field.value || []}
            onChange={field.onChange}
          />
        )}
      />
      {hasLeisure && (
        <Controller
          name="leisure_areas"
          control={form.control}
          render={({ field }) => (
            <ChipSelectMulti
              label={t('propertyDrawer.amenities.leisure')}
              options={leisureOptions}
              value={field.value || []}
              onChange={field.onChange}
            />
          )}
        />
      )}
      {hasFacilities && (
        <Controller
          name="facilities"
          control={form.control}
          render={({ field }) => (
            <ChipSelectMulti
              label={t('propertyDrawer.amenities.facilities')}
              options={facilitiesOptions}
              value={field.value || []}
              onChange={field.onChange}
            />
          )}
        />
      )}
      {hasSecurity && (
        <Controller
          name="security"
          control={form.control}
          render={({ field }) => (
            <ChipSelectMulti
              label={t('propertyDrawer.amenities.security')}
              options={securityOptions}
              value={field.value || []}
              onChange={field.onChange}
            />
          )}
        />
      )}
      {isCondominium && (
        <>
          <Controller
            name="condo_amenities"
            control={form.control}
            render={({ field }) => (
              <ChipSelectMulti
                label={t('propertyDrawer.amenities.condoLeisure')}
                options={condoAmenityOptions}
                value={field.value || []}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="condo_security"
            control={form.control}
            render={({ field }) => (
              <ChipSelectMulti
                label={t('propertyDrawer.amenities.condoSecurity')}
                options={condoSecurityOptions}
                value={field.value || []}
                onChange={field.onChange}
              />
            )}
          />
        </>
      )}
    </FormSection>
  );
}
